import { LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { observe } from '../common/decorators/observe.js'
import { injectLegacyLightDomStyles } from '../common/light-dom-styles.js'

import style from './AccordionHeader.css'

/**
 * Accordion Header is the heading that labels an
 * [Accordion Panel](/components/accordion-panel/). Following the WAI-ARIA
 * accordion pattern, its [Accordion Trigger](/components/accordion-trigger/)
 * button is wrapped in a heading element.
 *
 * @status draft
 * @category container
 * @lightDom true
 *
 * @slot - Default slot for an [Accordion Trigger](/components/accordion-trigger/).
 */
@customElement('nord-accordion-header')
export default class AccordionHeader extends LitElement {
  /**
   * The heading level exposed to assistive technology, matching the surrounding
   * document outline.
   */
  @property({ reflect: true, type: Number }) level = 3

  connectedCallback() {
    super.connectedCallback()
    injectLegacyLightDomStyles(this, style)
    this.setAttribute('role', 'heading')
    this.setAttribute('aria-level', `${this.level}`)
  }

  // Render in the light DOM so the trigger stays the element's own content and
  // the injected stylesheet applies (no shadow boundary, like Table).
  protected createRenderRoot() {
    return this
  }

  @observe('level')
  protected handleLevelChange() {
    this.setAttribute('aria-level', `${this.level}`)
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-accordion-header': AccordionHeader
  }
}
